/**
 * This file contains functions to validate portions of a Procedure object. Not all functions
 * in this file need to produce ProcedureValidationErrors.
 */

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PROCEDURE_PRODUCT_LIMITS} from 'procedures/design/ProcedureDesignConstants';
import {validateMethodDilutionPlan} from 'procedures/design/validation/private-validation-helpers/validateDilutionPlan';
import {
  extractDuplicateNames,
  ProcedureValidationContext,
} from 'procedures/design/validation/procedureValidationUtils';
import {Method, MethodNode, ProcedureFlowchart} from 'procedures/ProcedureDomainTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {extractMethodNodeFromProcessNode} from 'procedures/processes/MethodNodeUtils';
import {ProcessFlowchartForValidation} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';

import {validateProcedureMaterialEdges} from './private-validation-helpers/validateEdges';
import {validateProcedureMethodParameterDefinitions} from './private-validation-helpers/validateParameters';
import {validateProcedureTestDefinitions} from './private-validation-helpers/validateTestDefinitions';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
  VALIDATION_ERROR_WITH_NO_DISPLAYED_OBJECT,
} from './ProcedureValidationTypes';

export function isNameEmpty(name: string | null | undefined): boolean {
  return name == null || name.trim() === '';
}

/**
 * @package Exported for testing
 */
export function validateProcedureMethodSteps(method: Method): Array<ProcedureValidationError> {
  const errors: Array<ProcedureValidationError> = [];
  const stepCidsByName: Record<string, Array<string>> = {}; // {[name]: [cid1, cid2]}

  for (const group of method.stepGroups) {
    for (const step of group.steps) {
      const name = step.name.trim();
      stepCidsByName[name] = [...(stepCidsByName[name] || []), step.cid];
    }
  }

  // Empty step names
  for (const emptyStepCid of stepCidsByName[''] || []) {
    errors.push({
      errorMessage: 'Step names are required',
      type: ProcedureValidationErrorType.INVALID_NAME,
      primaryObject: {id: emptyStepCid, objectType: ProcedureValidationObjectType.STEP},
      parentObjects: [{id: method.cid, objectType: ProcedureValidationObjectType.METHOD}],
    });
  }
  delete stepCidsByName[''];

  // Duplicate step names
  for (const cids of Object.values(stepCidsByName)) {
    if (cids.length > 1) {
      for (const cidOfStepWithDuplicateName of cids) {
        errors.push({
          errorMessage: 'Duplicate step name',
          type: ProcedureValidationErrorType.INVALID_NAME,
          primaryObject: {id: cidOfStepWithDuplicateName, objectType: ProcedureValidationObjectType.STEP},
          parentObjects: [{id: method.cid, objectType: ProcedureValidationObjectType.METHOD}],
        });
      }
    }
  }

  // Deleted parameters
  const validParameterVersionIds: Array<string> = [];
  method.dataParameterSettings.forEach(
    (setting) =>
      setting.dataParameterVersion?.id && validParameterVersionIds.push(setting.dataParameterVersion?.id)
  );
  method.materialInputSettings.forEach(
    (setting) =>
      setting.materialInputVersion?.id && validParameterVersionIds.push(setting.materialInputVersion.id)
  );
  method.materialOutputSettings.forEach(
    (setting) =>
      setting.materialOutputVersion?.id && validParameterVersionIds.push(setting.materialOutputVersion.id)
  );
  method.equipmentSettings.forEach(
    (setting) => setting.equipmentVersion?.id && validParameterVersionIds.push(setting.equipmentVersion.id)
  );

  const parameterUsages = method.parameterUsages;
  Object.keys(parameterUsages).forEach((id) => {
    if (!validParameterVersionIds.includes(id)) {
      parameterUsages[id]?.forEach((usage) => {
        const stepCid = usage.stepCid;
        const stepName = usage.stepName;
        errors.push({
          errorMessage: `Parameter referenced in ${stepName} has been deleted. Please adjust any possible formula errors.`,
          type: ProcedureValidationErrorType.DELETED_PARAMETER,
          primaryObject: {id: stepCid, objectType: ProcedureValidationObjectType.STEP},
          parentObjects: [{id: method.cid, objectType: ProcedureValidationObjectType.METHOD}],
        });
      });
    }
  });
  return errors;
}

/**
 * @package Exported for testing
 */
export function validateProcedureMethods(
  methodNodes: Array<MethodNode>,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  const methodUIString = getProcedureUIString(ProceduresStringKeys.METHOD, context.procedureType);
  const allDuplicateMethodNames = extractDuplicateNames(methodNodes.map((node) => node.name));
  const methodNameErrors: Array<ProcedureValidationError> = methodNodes.flatMap((node) => {
    if (isNameEmpty(node.name)) {
      return {
        errorMessage: `${methodUIString} names are required`,
        type: ProcedureValidationErrorType.INVALID_NAME,
        // We surface the error into global lists, but don't associate it with the method (i.e., it doesn't get a red outline)
        primaryObject: {
          id: VALIDATION_ERROR_WITH_NO_DISPLAYED_OBJECT,
          objectType: ProcedureValidationObjectType.METHOD,
        },
        parentObjects: [],
      };
    } else if (allDuplicateMethodNames.includes(node.name)) {
      return {
        errorMessage: `${methodUIString} names must be unique`,
        type: ProcedureValidationErrorType.NON_UNIQUE_NAME,
        primaryObject: {
          id: node.cid,
          objectType: ProcedureValidationObjectType.METHOD,
        },
        parentObjects: [],
      };
    }
    return [];
  });

  const parameterErrors: Array<ProcedureValidationError> = methodNodes.flatMap((node) =>
    validateProcedureMethodParameterDefinitions(node, context)
  );

  const stepErrors: Array<ProcedureValidationError> = methodNodes.flatMap((node) =>
    validateProcedureMethodSteps(node)
  );

  const testDefinitionErrors: Array<ProcedureValidationError> = methodNodes.flatMap((node) =>
    validateProcedureTestDefinitions(node, methodNodes)
  );

  const dilutionPlanErrors: Array<ProcedureValidationError> = methodNodes.flatMap((node) =>
    validateMethodDilutionPlan(node)
  );

  return methodNameErrors.concat(parameterErrors, stepErrors, testDefinitionErrors, dilutionPlanErrors);
}

function makeProductLimitError(errorMessage: string, methodCid?: string): ProcedureValidationError {
  return {
    errorMessage,
    type: ProcedureValidationErrorType.PROCEDURE_PRODUCT_LIMITS,
    primaryObject: {
      id: VALIDATION_ERROR_WITH_NO_DISPLAYED_OBJECT,
      objectType: ProcedureValidationObjectType.PROCEDURE,
    },
    parentObjects:
      methodCid != null ? [{id: methodCid, objectType: ProcedureValidationObjectType.METHOD}] : [],
  };
}

function validateProcedureProductLimits(
  methodNodes: Array<MethodNode>,
  procedureType: ProcedureType
): Array<ProcedureValidationError> {
  const errors: Array<ProcedureValidationError> = [];
  let parameterCount = 0;
  let testDefinitionCount = 0;
  const methodUIString = getProcedureUIString(ProceduresStringKeys.METHOD, procedureType, {
    stringCase: StringCase.LOWER,
  });
  for (const node of methodNodes) {
    parameterCount +=
      node.materialInputSettings.length +
      node.materialOutputSettings.length +
      node.dataParameterSettings.length +
      node.equipmentSettings.length;
    testDefinitionCount += node.testDefinitions.length;

    if (node.materialInputSettings.length > PROCEDURE_PRODUCT_LIMITS.MATERIAL_INPUTS_PER_METHOD) {
      errors.push(
        makeProductLimitError(
          `Number of material inputs in ${methodUIString} "${node.name}" exceeds ${PROCEDURE_PRODUCT_LIMITS.MATERIAL_INPUTS_PER_METHOD}.`,
          node.cid
        )
      );
    }
    if (node.materialOutputSettings.length > PROCEDURE_PRODUCT_LIMITS.MATERIAL_OUTPUTS_PER_METHOD) {
      errors.push(
        makeProductLimitError(
          `Number of material outputs in ${methodUIString} "${node.name}" exceeds ${PROCEDURE_PRODUCT_LIMITS.MATERIAL_OUTPUTS_PER_METHOD}.`,
          node.cid
        )
      );
    }
    if (node.dataParameterSettings.length > PROCEDURE_PRODUCT_LIMITS.DATA_PARAMETERS_PER_METHOD) {
      errors.push(
        makeProductLimitError(
          `Number of data parameters in ${methodUIString} "${node.name}" exceeds ${PROCEDURE_PRODUCT_LIMITS.DATA_PARAMETERS_PER_METHOD}.`,
          node.cid
        )
      );
    }
    if (node.equipmentSettings.length > PROCEDURE_PRODUCT_LIMITS.EQUIPMENT_PER_METHOD) {
      errors.push(
        makeProductLimitError(
          `Number of equipment in ${methodUIString} "${node.name}" exceeds ${PROCEDURE_PRODUCT_LIMITS.EQUIPMENT_PER_METHOD}.`,
          node.cid
        )
      );
    }
  }
  if (parameterCount > PROCEDURE_PRODUCT_LIMITS.PARAMETERS_PER_PROCEDURE) {
    const procedureUIString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.LOWER,
    });
    errors.push(
      makeProductLimitError(
        `Number of definitions in your ${procedureUIString} exceeds ${PROCEDURE_PRODUCT_LIMITS.PARAMETERS_PER_PROCEDURE}.`
      )
    );
  }
  if (testDefinitionCount > PROCEDURE_PRODUCT_LIMITS.TEST_DEFINITIONS_PER_PROCEDURE) {
    const procedureUIString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.LOWER,
    });
    errors.push(
      makeProductLimitError(
        `Number of test definitions in your ${procedureUIString} exceeds ${PROCEDURE_PRODUCT_LIMITS.TEST_DEFINITIONS_PER_PROCEDURE}.`
      )
    );
  }
  return errors;
}

export function validateProcedure(
  flowchart: ProcedureFlowchart,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  const errors: Array<ProcedureValidationError> = [];
  if (flowchart.nodes.length === 0) {
    errors.push({
      errorMessage: `At least one ${getProcedureUIString(ProceduresStringKeys.METHOD, context.procedureType, {stringCase: StringCase.LOWER})} is required.`,
      type: ProcedureValidationErrorType.NO_UNIT_OPERATIONS,
      primaryObject: {
        id: VALIDATION_ERROR_WITH_NO_DISPLAYED_OBJECT,
        objectType: ProcedureValidationObjectType.PROCEDURE,
      },
      parentObjects: [],
    });
  }

  return [
    ...errors,
    ...validateProcedureMethods(flowchart.nodes, context),
    ...validateProcedureMaterialEdges(flowchart, context.procedureType),
    ...validateProcedureProductLimits(flowchart.nodes, context.procedureType),
  ];
}

/** Validates PoW procedure starting from ProcessFlowchartForValidation.
 *
 * This function focuses on validating the contents of the method nodes and
 * provides a mapping from method node cid to task node id. This can be used to
 * map errors back to the correct task node.
 *
 * This function does not validate material edges given we rely on the processes
 * platform to validate them. See processes/graph-builder-processes/validation/validateProcessFlowchartRules.ts
 * for more details on existing validation rules for processes platform and how material edges are validated.
 *
 * @param processFlowchartForValidation - The process flowchart to validate.
 * @param context - The procedure validation context.
 * @returns an object containing the procedure validation errors and a mapping from method node cid to task node id.
 */
export function validateProcessFlowchartForProcedure(
  processFlowchartForValidation: ProcessFlowchartForValidation,
  context: ProcedureValidationContext
): {
  procedureValidationErrors: Array<ProcedureValidationError>;
  methodNodeCidToTaskNodeId: Record<string, string>;
} {
  const methodNodeCidToTaskNodeId: Record<string, string> = {};
  const methodNodes = [];

  // Convert process nodes to procedure nodes
  for (const node of processFlowchartForValidation.nodes) {
    const procedureNode = extractMethodNodeFromProcessNode(node);
    if (procedureNode != null) {
      methodNodeCidToTaskNodeId[procedureNode.cid] = node.id;
      methodNodes.push(procedureNode);
    }
  }

  const procedureValidationErrors = [
    ...validateProcedureMethods(methodNodes, context),
    ...validateProcedureProductLimits(methodNodes, context.procedureType),
  ];

  return {
    procedureValidationErrors,
    methodNodeCidToTaskNodeId,
  };
}
